import { COOKIE_ECHO_USER, CURRENCY, PRODUCT, PUBLIC_META, PUBLIC_PRICING_PAGE_DETAILS } from 'pricing-pages-api/EarlyRequesterNames';
import { getPublicFeatureFlagsV2Rpc, getPublicPricingPageDetailsRpc } from 'pricing-pages-api/__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc';
import { evaluatePublicRpc } from 'pricing-pages-api/__generated__/chirp/com/hubspot/purchase/motions/rpc/PurchaseMotionsPublicEvaluationService';
import { getPreferredLanguage } from 'pricing-pages-lib/utils/getPreferredLanguage';
import { getIsQAProxy } from 'pricing-pages-lib/utils/proxyUtils';
import { getBooleanFromString, getQueryParam, getShortLinkForRedirect, getUtk } from 'pricing-pages-ui/earlyRequester/earlyRequesterUtils';
import { laboratoryClient } from 'pricing-pages-ui/utils/laboratoryClient';
import { getCountryCodeFromLocale } from 'pricing-pages-ui/v2/utils/countryCodeUtils';
import quickFetch from 'quick-fetch';
import { makeChirpEarlyRequest } from 'rpc-client-utils';
(function (_getCountryCodeFromLo) {
  const browserLocale = getPreferredLanguage().toLowerCase() || 'en-us';
  const isQAProxy = getIsQAProxy();
  const utk = getUtk();
  window.__HUBSPOT_UTK = utk; // global var so we can access in the app code

  // const UTK_OR_UUID = getStashedUuid() || UTK || generateUuid(); // will exist & be persisted for everyone

  const countryCode = (_getCountryCodeFromLo = getCountryCodeFromLocale()) !== null && _getCountryCodeFromLo !== void 0 ? _getCountryCodeFromLo : '';
  const countryCodeParam = countryCode ? `&countryCode=${countryCode}` : '';
  const cookieUrl = isQAProxy ? /* eslint-disable-next-line hubspot-dev/prefer-hubspot-url-utils */
  'https://api.hubspotqa.com/cookie-echo/v1/user' : quickFetch.getApiUrl('/cookie-echo/v1/user', false);
  // Redirect detection
  quickFetch.makeEarlyRequest(COOKIE_ECHO_USER, {
    url: cookieUrl,
    type: 'GET',
    dataType: 'json',
    // Relatively quick timeout since missing the redirect is not a big deal, and we don't want to block pageload long
    // This might mean that redirects skew towards NorthAm and those with fast internet connections
    timeout: 2000,
    whenFinished(response) {
      // This endpoint will 401 if you're not logged in so wont hit this block
      const email = response.email;

      // by default we don't redirect hubspot users. We can override that by having hsRedirect=true in query param
      const hsRedirect = getQueryParam('hsRedirect');
      const shouldRedirectHubSpotUsers = getBooleanFromString(hsRedirect || '');
      if (email.indexOf('@hubspot.com') > -1 && !shouldRedirectHubSpotUsers) {
        // Don't redirect hubspot users. Important for sales
        // who want a consistent demo experience on hubspot.com
        return;
      }

      // Use redirect so we don't keep the public page in history
      const redirectUrl = getShortLinkForRedirect(window.location.href);
      window.location.replace(redirectUrl);
    }
  });

  // We include hostname b/c this request is cached. If we switch langs, we'll get a mismatched 'Access-Control-Allow-Origin'
  // which will cause an error, so we want to make sure switching domains doesn't read from the cache

  const productUrl = isQAProxy ? ` https://api.hubspotqa.com/monetization/v3/product?host=${window.location.hostname}${countryCodeParam}` : quickFetch.getApiUrl(`/monetization/v3/product?host=${window.location.hostname}${countryCodeParam}`, false);
  quickFetch.makeEarlyRequest(PRODUCT, {
    url: productUrl,
    type: 'GET',
    dataType: 'json',
    withCredentials: true,
    timeout: 12000
  });
  void makeChirpEarlyRequest(Object.assign({}, evaluatePublicRpc, {
    earlyRequest: true
  }), {
    request: {
      locations: ['MERCH_CARD', 'CALCULATOR', 'HUBSPOT_CREDITS_MERCH_CARD_SMALL', 'HUBSPOT_CREDITS_MERCH_CARD_MEDIUM_LARGE'],
      productApiNames: [],
      requestedCountryCode: countryCode
    }
  });
  const preferredCountryCode = getCountryCodeFromLocale();
  void makeChirpEarlyRequest(Object.assign({}, getPublicPricingPageDetailsRpc, {
    earlyRequest: true,
    earlyRequestName: PUBLIC_PRICING_PAGE_DETAILS
  }), {
    request: {
      browserLocale,
      preferredCountryCode
    }
  });
  let publicMetaUrl = isQAProxy ? /* eslint-disable-next-line hubspot-dev/prefer-hubspot-url-utils */
  'https://api.hubspotqa.com/monetization/v3/public-pricing' : quickFetch.getApiUrl('/monetization/v3/public-pricing', false);
  publicMetaUrl = `${publicMetaUrl}/${browserLocale}`;

  // the (window as any).COUNTRY_CODE_FROM_LOCALE should be set BEFORE the early requester script is loaded in the base jade file
  if (preferredCountryCode) {
    publicMetaUrl = `${publicMetaUrl}?preferredCountry=${preferredCountryCode}`;
  }
  quickFetch.makeEarlyRequest(PUBLIC_META, {
    url: publicMetaUrl,
    type: 'GET',
    dataType: 'json',
    withCredentials: true,
    timeout: 12000
  });
  void makeChirpEarlyRequest(Object.assign({}, getPublicFeatureFlagsV2Rpc, {
    earlyRequest: true
  }), {
    request: {
      utk,
      browserLocale,
      preferredCountryCode
    }
  });

  // @ts-expect-error mock is only set in unit tests
  if (!quickFetch.mockRequests) {
    laboratoryClient.resolve();
  }
  const currencyUrl = isQAProxy ? /* eslint-disable-next-line hubspot-dev/prefer-hubspot-url-utils */
  'https://api.hubspotqa.com/monetization/v3/currency/public' : quickFetch.getApiUrl('/monetization/v3/currency/public', false);
  const currencyUrlToUse = preferredCountryCode ? `${currencyUrl}/${browserLocale}?preferredCountryCode=${preferredCountryCode}` : `${currencyUrl}/${browserLocale}`;
  quickFetch.makeEarlyRequest(CURRENCY, {
    url: currencyUrlToUse,
    type: 'GET',
    dataType: 'json',
    withCredentials: true,
    timeout: 12000
  });
})();